import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Gauge, ShieldCheck, Search, Smartphone, Globe, FileSearch,
  ArrowRight, CheckCircle2, XCircle, AlertCircle, Loader2, Mail,
  Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";

const ICONS = {
  speed: Gauge,
  https: ShieldCheck,
  viewport: Smartphone,
  title: FileSearch,
  description: FileSearch,
  h1: FileSearch,
  alt: Search,
  og: Globe,
  favicon: Globe,
  robots: Search,
  sitemap: Search,
  wordcount: FileSearch,
};

const ORDER = [
  "speed", "https", "viewport",
  "title", "description", "h1",
  "alt", "og", "favicon",
  "robots", "sitemap", "wordcount",
];

const Ring = ({ score, grade }) => {
  const r = 56;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.min(Math.max(score, 0), 100) / 100) * c;
  const color = score >= 70 ? "var(--brand-lime, #A4C539)" : score >= 50 ? "var(--brand-cyan, #1DA9DA)" : "#E11D48";
  return (
    <div className="relative w-36 h-36 shrink-0" data-testid="check-score-ring">
      <svg viewBox="0 0 140 140" className="w-full h-full -rotate-90">
        <circle cx="70" cy="70" r={r} stroke="#E5EAF0" strokeWidth="10" fill="none" />
        <circle
          cx="70" cy="70" r={r}
          stroke={color}
          strokeWidth="10"
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 1.2s ease-out" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-display font-black text-4xl text-ink leading-none">{score}</div>
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted mt-1">/ 100 · {grade}</div>
      </div>
    </div>
  );
};

const CheckRow = ({ check, k }) => {
  const Icon = ICONS[k] || FileSearch;
  return (
    <div
      className={`flex items-start gap-4 p-5 border ${check.ok ? "border-line bg-white" : "border-accent/30 bg-soft"}`}
      data-testid={`check-row-${k}`}
    >
      <div className={`w-10 h-10 shrink-0 flex items-center justify-center border ${check.ok ? "bg-white border-line text-primary" : "bg-white border-accent/40 text-accent"}`}>
        <Icon size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-display font-bold text-ink text-base">{check.label}</span>
          {check.ok ? (
            <CheckCircle2 size={16} className="text-primary" />
          ) : (
            <XCircle size={16} className="text-accent" />
          )}
        </div>
        <div className="mt-1 font-mono text-xs text-muted truncate">{String(check.value)}</div>
        <div className={`mt-2 text-sm leading-relaxed ${check.ok ? "text-muted" : "text-ink"}`}>{check.hint}</div>
      </div>
    </div>
  );
};

export const WebsiteCheckForm = ({ compact = false }) => {
  const [form, setForm] = useState({ url: "", email: "", name: "", company: "", consent: false });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.consent) {
      setError("Bitte bestätigen Sie die Datenschutzhinweise.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/website-check", {
        url: form.url,
        email: form.email,
        name: form.name || null,
        company: form.company || null,
        consent: form.consent,
      });
      setResult(data);
      if (typeof window !== "undefined") {
        setTimeout(() => {
          document.getElementById("check-result")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Fehler beim Check.";
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="website-check-form" className={compact ? "" : "w-full"}>
      <form onSubmit={submit} className="bg-white border border-line p-6 sm:p-8 shadow-soft">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-primary">
          <Sparkles size={14} /> Kostenfrei · 60 Sekunden · keine Anmeldung
        </div>
        <h3 className="mt-3 font-display font-black text-2xl sm:text-3xl tracking-tightest text-ink">
          Wie gut ist Ihre Webseite wirklich?
        </h3>
        <p className="mt-2 text-sm text-muted leading-relaxed">
          Geben Sie Ihre Domain ein – wir prüfen Performance, SEO, Mobile und Sicherheit. Detail-Report kommt direkt per E-Mail.
        </p>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block sm:col-span-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted">Ihre Webseite *</span>
            <input
              type="text"
              required
              value={form.url}
              onChange={(e) => set("url", e.target.value)}
              placeholder="ihre-firma.de"
              data-testid="check-input-url"
              className="mt-2 w-full bg-bg border border-line px-4 py-3 focus:border-primary outline-none font-mono text-sm"
            />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted">E-Mail *</span>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder="ihre@email.de"
              data-testid="check-input-email"
              className="mt-2 w-full bg-bg border border-line px-4 py-3 focus:border-primary outline-none"
            />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted">Name (optional)</span>
            <input
              type="text"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Max Mustermann"
              data-testid="check-input-name"
              className="mt-2 w-full bg-bg border border-line px-4 py-3 focus:border-primary outline-none"
            />
          </label>
          {!compact && (
            <label className="block sm:col-span-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted">Firma (optional)</span>
              <input
                type="text"
                value={form.company}
                onChange={(e) => set("company", e.target.value)}
                placeholder="Mustermann GmbH"
                data-testid="check-input-company"
                className="mt-2 w-full bg-bg border border-line px-4 py-3 focus:border-primary outline-none"
              />
            </label>
          )}
        </div>

        <label className="mt-5 flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.consent}
            onChange={(e) => set("consent", e.target.checked)}
            data-testid="check-input-consent"
            className="mt-1 w-4 h-4 accent-[#1A4D6E]"
          />
          <span className="text-xs text-muted leading-relaxed">
            Ich bin einverstanden, dass IT-Servicepoint meine Daten zur Erstellung und Zusendung des Reports verwendet. Details in der{" "}
            <Link to="/datenschutz" className="text-primary underline">Datenschutzerklärung</Link>.
          </span>
        </label>

        {error && (
          <div className="mt-4 flex items-start gap-2 border border-accent/40 bg-accent/10 p-3 text-sm text-ink" data-testid="check-error">
            <AlertCircle size={16} className="text-accent shrink-0 mt-0.5" /> {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="check-submit"
          className="mt-6 group inline-flex w-full sm:w-auto items-center justify-center gap-2 px-7 py-3.5 bg-primary text-white font-medium hover:bg-primary-hover transition-colors shadow-soft disabled:opacity-60"
        >
          {loading ? (
            <>
              <Loader2 size={18} className="animate-spin" /> Wir prüfen Ihre Seite …
            </>
          ) : (
            <>
              Kostenfrei prüfen
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </>
          )}
        </button>
      </form>

      {result && (
        <div id="check-result" className="mt-12" data-testid="check-result">
          {!result.ok ? (
            <div className="border border-accent/40 bg-soft p-8">
              <div className="flex items-center gap-3">
                <AlertCircle size={22} className="text-accent" />
                <h3 className="font-display font-black text-2xl text-ink">Wir konnten Ihre Seite nicht erreichen</h3>
              </div>
              <p className="mt-3 text-muted">{result.error || "Bitte prüfen Sie die URL und versuchen Sie es erneut."}</p>
              <p className="mt-2 font-mono text-xs text-muted">{result.url}</p>
            </div>
          ) : (
            <div className="border border-line bg-white">
              {/* Result header */}
              <div className="p-6 sm:p-10 border-b border-line bg-soft">
                <div className="flex flex-col lg:flex-row gap-8 items-start lg:items-center">
                  <Ring score={result.score} grade={result.grade} />
                  <div className="flex-1">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-primary">Ihr Webseiten-Score</div>
                    <h3 className="mt-2 font-display font-black text-2xl sm:text-3xl text-ink break-all">{result.final_url}</h3>
                    <p className="mt-3 text-muted leading-relaxed">
                      Wir haben <strong className="text-ink">{Object.keys(result.checks || {}).length}</strong> Faktoren geprüft. Geladen in <strong className="text-ink">{result.load_ms} ms</strong>. Detail-Report mit Empfehlungen liegt {result.emailed ? (
                        <>in Ihrem Postfach <span className="text-primary">({form.email})</span>.</>
                      ) : (
                        <>unten und wird Ihnen bei Bedarf gerne per E-Mail zugesendet.</>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* KI-Einschätzung */}
              {result.ai_summary && (
                <div className="mx-6 sm:mx-10 mt-6 border border-line bg-ink text-white p-6" data-testid="check-ai-summary">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent mb-3">Persönliche KI-Einschätzung · Claude</div>
                  <p className="leading-relaxed whitespace-pre-line text-white/90 text-sm sm:text-base">{result.ai_summary}</p>
                </div>
              )}

              {/* Check rows */}
              <div className="p-6 sm:p-10 grid grid-cols-1 md:grid-cols-2 gap-4">
                {ORDER.filter((k) => result.checks?.[k]).map((k) => (
                  <CheckRow key={k} k={k} check={result.checks[k]} />
                ))}
              </div>

              {/* CTA */}
              <div className="border-t border-line p-6 sm:p-10 bg-primary text-white">
                <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between">
                  <div>
                    <h4 className="font-display font-black text-2xl sm:text-3xl tracking-tightest">
                      Lassen Sie uns die Schwachstellen beheben.
                    </h4>
                    <p className="mt-2 text-white/80 max-w-2xl">
                      30 Minuten Erstgespräch · konkreter Maßnahmenplan · Festpreis-Angebot in 3 Werktagen.
                    </p>
                  </div>
                  <Link
                    to="/kontakt"
                    data-testid="check-result-cta"
                    className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-accent text-ink font-medium hover:bg-accent/90 transition-colors shrink-0"
                  >
                    Kostenloses Erstgespräch <ArrowRight size={18} />
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
